import * as React from "react";
import { Eye, EyeOff } from "lucide-react";

import { cn } from "@/lib/utils";

import { Button } from "./button";
import { Input } from "./input";
import {
  getPasswordInputType,
  getPasswordToggleLabel,
  shouldTemporarilyRevealPassword,
} from "./password-input-state";

type PasswordInputProps = Omit<React.ComponentProps<"input">, "type"> & {
  revealWhileTyping?: boolean;
  revealDurationMs?: number;
};

function PasswordInput({
  className,
  revealWhileTyping = false,
  revealDurationMs = 500,
  onChange,
  value,
  ...props
}: PasswordInputProps) {
  const [isVisible, setIsVisible] = React.useState(false);
  const [isTemporarilyRevealing, setIsTemporarilyRevealing] = React.useState(false);
  const revealTimeoutRef = React.useRef<number | null>(null);

  React.useEffect(() => {
    return () => {
      if (revealTimeoutRef.current !== null) {
        window.clearTimeout(revealTimeoutRef.current);
      }
    };
  }, []);

  const currentValue = typeof value === "string" ? value : "";
  const inputType = getPasswordInputType({ isVisible, isTemporarilyRevealing });

  return (
    <div className="relative">
      <Input
        {...props}
        value={value}
        type={inputType}
        className={cn("pr-10", className)}
        onChange={(event) => {
          onChange?.(event);

          const nextValue = event.target.value;
          if (
            shouldTemporarilyRevealPassword({
              revealWhileTyping,
              isVisible,
            }) &&
            nextValue.length > currentValue.length
          ) {
            setIsTemporarilyRevealing(true);
            if (revealTimeoutRef.current !== null) {
              window.clearTimeout(revealTimeoutRef.current);
            }
            revealTimeoutRef.current = window.setTimeout(() => {
              setIsTemporarilyRevealing(false);
              revealTimeoutRef.current = null;
            }, revealDurationMs);
          }
        }}
      />
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="absolute right-1 top-1/2 h-7 w-7 -translate-y-1/2 text-muted-foreground hover:text-foreground"
        onClick={() => {
          if (revealTimeoutRef.current !== null) {
            window.clearTimeout(revealTimeoutRef.current);
            revealTimeoutRef.current = null;
          }
          setIsTemporarilyRevealing(false);
          setIsVisible((current) => !current);
        }}
        aria-label={getPasswordToggleLabel(isVisible)}
      >
        {isVisible ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
      </Button>
    </div>
  );
}

export { PasswordInput };
